// Moved from `src/hooks/mutation/useMutateToggleTeamMfa.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: both toasts, the Rematch `trackToggleTeamMfa`
// dispatch, and `invalidateCurrentUser()` (see the sibling consent hook for why
// that one crosses as a port rather than being reimplemented). The legacy
// `useQueryUser` read fed only the analytics `adminId`, so it moves to the
// delegate with the dispatch.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts';
import { toggleTeamMfa } from '../api/teams';

export interface ToggleTeamMfaVariables {
  mfaEnabled: boolean;
  teamId: string;
}

export interface UseMutateToggleTeamMfaOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /**
   * App-reaction port: fired where the success toast and the Rematch
   * `trackToggleTeamMfa` dispatch fired, in that order.
   */
  onMfaToggled?: (variables: ToggleTeamMfaVariables) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onMfaToggleError?: (error: Error) => void;
}

/** PATCH /teams/{team_id}/mfa */
export function useMutateToggleTeamMfa(options?: UseMutateToggleTeamMfaOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggleTeamMfa'],
    mutationFn: async (input: ToggleTeamMfaVariables) => {
      const data = await toggleTeamMfa({
        teamId: input.teamId,
        mfaEnabled: input.mfaEnabled,
      });
      return data;
    },
    onSuccess: async (_, input) => {
      // tracking
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });
      options?.onCurrentUserRefreshNeeded?.();
      options?.onMfaToggled?.(input);
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onMfaToggleError?.(error);
    },
  });
}
